import * as z from "zod";
import { applyScopeGrantPatch } from "../rbac/applyScopeGrantPatch";
import { SHAREABLE_SCOPE_KINDS } from "../rbac/permissions";
import { SCOPE_MANAGE_GRANTS_PERMISSION } from "../rbac/roles";
import { grantEntry, removalEntry } from "../rbac/validation";
import { monitoringAnalytics } from "./monitoringAnalytics";
import { mutator } from "./mutator";

// Adds and removes a scope's grants additively, in one transaction, for any
// shareable scope kind. Reads the current grants inside the transaction and only
// inserts the additions and deletes the removals; pre-existing grants are never
// re-authorized. autoGrantConnectors/autoGrantSkills are honored only for spaces
// (the sole cross-resource dependent); the shared core drops connector usages on
// removal.
export const patchScopeGrants = mutator({
  stripKeys: { analytics: Object.keys(monitoringAnalytics.shape) },
  schema: z.object({
    analytics: monitoringAnalytics.optional(),
    scopeId: z.string(),
    scopeKind: z.enum(SHAREABLE_SCOPE_KINDS),
    add: z.array(grantEntry),
    remove: z.array(removalEntry),
    autoGrantConnectors: z.boolean().optional(),
    autoGrantSkills: z.boolean().optional(),
  }),
  // Resolve principals + custom roles from the `add` array (the shared
  // grant*Resolve helpers read a field named `grants`; this mutator's is `add`).
  resolve: {
    grantUsers: { kind: "user", from: "add", field: "userId" },
    grantUserGroups: { kind: "userGroup", from: "add", field: "userGroupId" },
    grantServiceAccounts: {
      kind: "serviceAccount",
      from: "add",
      field: "serviceAccountId",
    },
    grantCustomRoles: {
      kind: "customRole",
      from: "add",
      field: "customRoleId",
    },
  },
  permissions: [
    {
      permission: ({ args }) => SCOPE_MANAGE_GRANTS_PERMISSION[args.scopeKind],
      scopeId: ({ args }) => args.scopeId as string,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const { scopeId, scopeKind, add, remove, grantCustomRoles } = args;
    await applyScopeGrantPatch({
      tx,
      ctx,
      scopeId,
      scopeKind,
      add,
      remove,
      customRoles: grantCustomRoles,
      autoGrant: {
        connectors: args.autoGrantConnectors ?? false,
        skills: args.autoGrantSkills ?? false,
      },
    });
  },
});
